import React, {
  useImperativeHandle,
  useState,
  forwardRef,
  useEffect,
} from "react";
import axios from "axios";
import API_BASE_URL from "../config";

const SETTINGS_DEFAULTS = {
  requiresConfirmation: false,
  disableCancelling: false,
  disableRescheduling: false,
  requiresEmailVerification: false,
  hideNotesInCalendar: false,
  hideEventDetails: false,
  redirectOnBooking: false,
  redirectUrl: "",
  lockTimezone: false,
  eventTypeColor: "#4285f4",
};
axios.defaults.withCredentials = true;
axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem(
  "token"
)}`;
const SettingsPanel = forwardRef(({ onChange, eventId }, ref) => {
  const [settings, setSettings] = useState(SETTINGS_DEFAULTS);
  const [loading, setLoading] = useState(true);

  useImperativeHandle(ref, () => ({
    getState: () => settings,
  }));

  // Load settings using axios
  useEffect(() => {
    if (!eventId) return;

    axios
      .get(`${API_BASE_URL}/event/${eventId}/settings`)
      .then((response) => {
        const data = response.data;
        const merged = {
          ...SETTINGS_DEFAULTS,
          requiresConfirmation: Boolean(Number(data.requires_confirmation)),
          disableCancelling: Boolean(Number(data.disable_cancelling)),
          disableRescheduling: Boolean(Number(data.disable_rescheduling)),
          requiresEmailVerification: Boolean(
            Number(data.email_verification_required)
          ),
          hideNotesInCalendar: Boolean(Number(data.hide_notes_in_calendar)),
          hideEventDetails: Boolean(Number(data.hide_event_details_shared)),
          redirectOnBooking: Boolean(Number(data.redirect_on_booking)),
          redirectUrl: data.redirectUrl || "",
          lockTimezone: Boolean(Number(data.lock_timezone)),
          eventTypeColor: data.event_color || "#4285f4",
        };
        setSettings(merged);
        if (onChange) onChange(merged);
        if (onChange) onChange(merged);
      })
      .catch((error) => {
        console.error("Error loading settings:", error);
        // Optional: Handle error UI here
      })
      .finally(() => setLoading(false));
  }, [eventId]);

  const handleToggle = (key) => {
    const updated = { ...settings, [key]: !settings[key] };
    setSettings(updated);
    if (onChange) onChange(updated);
  };

  const handleColorChange = (e) => {
    const updated = { ...settings, eventTypeColor: e.target.value };
    setSettings(updated);
    if (onChange) onChange(updated);
  };

  const handleRedirectUrlChange = (e) => {
    const updated = { ...settings, redirectUrl: e.target.value };
    setSettings(updated);
    if (onChange) onChange(updated);
  };

  if (loading) return <p>Loading settings...</p>;

  return (
    <div className="settings-panel">
      {[
        { key: "requiresConfirmation", label: "Requires confirmation" },
        { key: "disableCancelling", label: "Disable Cancelling" },
        { key: "disableRescheduling", label: "Disable Rescheduling" },
        {
          key: "requiresEmailVerification",
          label: "Requires booker email verification",
        },
        { key: "hideNotesInCalendar", label: "Hide notes in calendar" },
        {
          key: "hideEventDetails",
          label: "Hide calendar event details on shared calendars",
        },
        { key: "redirectOnBooking", label: "Redirect on booking" },
        { key: "lockTimezone", label: "Lock timezone on booking page" },
      ].map(({ key, label }) => (
        <div className="form-check form-switch toggle-row" key={key}>
          <input
            className="form-check-input"
            type="checkbox"
            id={`switch-${key}`}
            checked={settings[key]}
            onChange={() => handleToggle(key)}
          />
          <label className="form-check-label" htmlFor={`switch-${key}`}>
            {label}
          </label>

          {key === "redirectOnBooking" && settings.redirectOnBooking && (
            <input
              type="url"
              placeholder="Enter redirect URL"
              className="form-control mt-1"
              value={settings.redirectUrl}
              onChange={handleRedirectUrlChange}
            />
          )}
        </div>
      ))}

      <div className="toggle-row mt-4">
        <label className="form-label">Event type color</label>
        <input
          type="color"
          className="form-control form-control-color"
          value={settings.eventTypeColor}
          onChange={handleColorChange}
        />
      </div>
    </div>
  );
});

export default SettingsPanel;
